import {
  RadioButtonCheckedOutlined,
  RadioButtonUncheckedOutlined,
} from "@mui/icons-material";
import SearchIcon from "@mui/icons-material/Search";
import { Radio, InputAdornment, Typography } from "@mui/material";
import React from "react";

import { palette } from "~frontend/theme";

import { Checkbox } from "../Checkbox";
import TagChip from "../TagChip";
import {
  AutocompleteValue,
  selectedOptionIcon,
  FormikInMemoryCombobox,
  optionIcon,
} from "./InMemoryCombobox";

export const childOptionIcon = (
  <RadioButtonUncheckedOutlined
    fontSize="small"
    sx={{ color: palette.midGray }}
  />
);
export const childSelectedOptionIcon = (
  <RadioButtonCheckedOutlined
    fontSize="small"
    sx={{ color: palette.darkGreen }}
  />
);

const startAdornment: React.ReactElement = (
  <InputAdornment position="start">
    <SearchIcon />
  </InputAdornment>
);

function renderTags(
  value: AutocompleteValue[],
  getTagProps: any
): React.ReactElement[] {
  return value.map((option, index) => (
    <TagChip
      label={option.selectedLabel ?? option.label}
      {...getTagProps({ index })}
      onDelete={
        // remove the delete button, users must unselect from the dropdown
        undefined
      }
      key={`tag-chip-${index}-${option.id}`}
    />
  ));
}

function renderOption(
  props: React.HTMLAttributes<HTMLLIElement>,
  option: AutocompleteValue,
  { selected }: { selected: boolean }
): React.ReactElement {
  function optionContent(): React.ReactElement {
    if (option.type === "groupHeading") {
      return (
        <Typography variant="body2Bold" sx={{ color: palette.darkGreen }}>
          {option.label}
        </Typography>
      );
    }

    const CheckboxComponent = option.type === "groupMember" ? Radio : Checkbox;

    return (
      <>
        <CheckboxComponent
          icon={option.type === "groupMember" ? childOptionIcon : optionIcon}
          checkedIcon={
            option.type === "groupMember"
              ? childSelectedOptionIcon
              : selectedOptionIcon
          }
          size="small"
          style={{
            padding: 0,
            marginRight: 8,
            ...(option.type === "groupMember"
              ? {
                  marginLeft: 16,
                }
              : {}),
          }}
          checked={selected}
        />

        <TagChip label={option.label} />
      </>
    );
  }
  return (
    <li
      {...props}
      style={
        selected
          ? {
              backgroundColor: palette.lightGray,
              boxShadow: "0px 0px 0px 2px white inset",
            }
          : undefined
      }
    >
      {optionContent()}
    </li>
  );
}

export function FormikTagCombobox({
  name,
  label,
  bottomLabel,
  options,
}: {
  name: string;
  label: string;
  bottomLabel: string;
  options: AutocompleteValue[];
}): React.ReactElement {
  return (
    <FormikInMemoryCombobox
      name={name}
      label={label}
      bottomLabel={bottomLabel}
      options={options}
      renderTags={renderTags}
      startAdornment={startAdornment}
      renderOption={renderOption}
      transformNewValue={(newOptions, oldOptions) => {
        const oldOptionIds = new Set(oldOptions.map(o => o.id));
        const addedOptions = newOptions.filter(o => !oldOptionIds.has(o.id));

        // if a grouped option was added, unselect any other options from that group
        const groupIdsOfAddedOptions = new Set(
          addedOptions
            .filter(o => o.type === "groupMember" && o.groupId)
            .map(o => o.groupId as string)
        );

        if (groupIdsOfAddedOptions.size === 0) {
          return newOptions;
        }

        return newOptions.filter(o => {
          if (
            o.type === "groupMember" &&
            o.groupId &&
            groupIdsOfAddedOptions.has(o.groupId)
          ) {
            return addedOptions.some(ao => ao.id === o.id);
          }
          return true;
        });
      }}
      slotProps={{
        listbox: {
          sx: {
            '.MuiAutocomplete-option[aria-disabled="true"]': {
              opacity: 1,
            },
          },
        },
      }}
      filterOptions={(options, state) => {
        const search = state.inputValue.toLowerCase();

        // Group-based search:
        //   - If a group heading matches, show the whole group by adding it
        //     to matchedGroupIds.
        //   - If a group member matches, add the groupId to groupIdsWithMatchingMembers.
        const matchedGroupIds = new Set<string>();
        const groupIdsWithMatchingMembers = new Set<string>();
        options.forEach(option => {
          if (
            option.type === "groupHeading" &&
            option.label.toLowerCase().includes(search)
          ) {
            matchedGroupIds.add(option.id);
          }

          if (
            option.type === "groupMember" &&
            option.groupId &&
            option.label.toLowerCase().includes(search)
          ) {
            groupIdsWithMatchingMembers.add(option.groupId);
          }
        });

        // Now filter options.
        // - Top-level options are included if they match the search.
        // - Group headings are included if they are in matchedGroupIds or
        //   groupIdsWithMatchingMembers.
        // - Group members are included if they match the search or their
        //   groupId is in matchedGroupIds.
        return options.filter(option => {
          if (option.type === "groupHeading") {
            return (
              matchedGroupIds.has(option.id) ||
              groupIdsWithMatchingMembers.has(option.id)
            );
          }

          if (option.type === "groupMember") {
            return (
              (option.groupId && matchedGroupIds.has(option.groupId)) ||
              option.label.toLowerCase().includes(search)
            );
          }

          // top-level option
          return option.label.toLowerCase().includes(search);
        });
      }}
    />
  );
}
